/**
 * EMVCo PromptPay QR Generator (Client & Server compatible)
 * Compliant with Bank of Thailand (BOT) Standard QR Code Specification
 */
import QRCode from 'qrcode';

export function crc16(data: string): string {
  let crc = 0xFFFF;
  for (let i = 0; i < data.length; i++) {
    crc ^= (data.charCodeAt(i) << 8);
    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = ((crc << 1) ^ 0x1021) & 0xFFFF;
      } else {
        crc = (crc << 1) & 0xFFFF;
      }
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
}

export function formatTlv(tag: string, value: string): string {
  const len = value.length.toString().padStart(2, '0');
  return `${tag}${len}${value}`;
}

export interface PromptPayQrParams {
  promptPayId?: string; // Phone or Tax/National ID
  billerId?: string;    // Biller ID (15 digits)
  merchantCode?: string; // Merchant Code (e.g. QV353070BI06881536X)
  merchantName?: string; // Merchant Name (e.g. GIFFFORYOU.COM)
  amount?: number;      // THB Amount
  ref1?: string;        // Ref 1 (e.g. Order ID)
  ref2?: string;        // Ref 2 (e.g. Merchant Code or Customer ID)
  isDynamic?: boolean;
}

export function generatePromptPayPayload(options: PromptPayQrParams): string {
  const isDynamic = options.isDynamic !== false && (options.amount !== undefined && options.amount > 0);
  let payload = '';

  // Tag 00: Payload Format Indicator (01)
  payload += formatTlv('00', '01');

  // Tag 01: Point of Initiation Method (11: Static, 12: Dynamic)
  payload += formatTlv('01', isDynamic ? '12' : '11');

  const activeMerchantCode = options.merchantCode || 'QV353070BI06881536X';
  const activeMerchantName = options.merchantName || 'GIFFFORYOU.COM';

  // Tag 29 / Tag 30: Merchant Account Information
  if (options.billerId && options.billerId.trim().length >= 5) {
    // Bill Payment (Tag 30 - KTB ถุงเงิน & PromptPay Bill Payment)
    const biller = options.billerId.replace(/[^0-9]/g, '');
    let tag30Value = '';
    tag30Value += formatTlv('00', 'A000000677010112'); // AID for Bill Payment
    tag30Value += formatTlv('01', biller);             // Biller ID (010753700088205)

    // Subtag 02 (REF 1): Merchant Code (รหัสผู้ค้า) - ต้องเป็น QV353070BI06881536X เท่านั้น
    tag30Value += formatTlv('02', activeMerchantCode); // Ref 1: QV353070BI06881536X

    // Subtag 03 (REF 2): Order Reference (เลขคำสั่งซื้อ)
    let ref2Value = options.ref2 || '';
    if (!ref2Value && options.ref1 && options.ref1 !== activeMerchantCode && options.ref1 !== 'QV353070BI06881536X') {
      ref2Value = options.ref1;
    }
    if (ref2Value) {
      tag30Value += formatTlv('03', ref2Value.substring(0, 20)); // Ref 2: Order Reference (เลขคำสั่งซื้อ)
    }
    payload += formatTlv('30', tag30Value);
  } else {
    // Standard PromptPay (Tag 29)
    const rawId = (options.promptPayId || '0939678649').replace(/[^0-9]/g, '');
    let tag29Value = '';
    tag29Value += formatTlv('00', 'A000000677010111'); // AID for PromptPay

    if (rawId.length === 10 && rawId.startsWith('0')) {
      // Mobile Phone (0066 + 9 digits)
      const formattedPhone = '0066' + rawId.substring(1);
      tag29Value += formatTlv('01', formattedPhone);
    } else if (rawId.length === 13) {
      // National ID / Tax ID
      tag29Value += formatTlv('02', rawId);
    } else if (rawId.length === 15) {
      // E-Wallet ID
      tag29Value += formatTlv('03', rawId);
    } else {
      tag29Value += formatTlv('01', rawId);
    }
    payload += formatTlv('29', tag29Value);
  }

  // Tag 53: Transaction Currency (764 = THB)
  payload += formatTlv('53', '764');

  // Tag 54: Transaction Amount
  if (options.amount !== undefined && options.amount > 0) {
    const formattedAmount = options.amount.toFixed(2);
    payload += formatTlv('54', formattedAmount);
  }

  // Tag 58: Country Code (TH)
  payload += formatTlv('58', 'TH');

  // Tag 59: Merchant Name
  payload += formatTlv('59', activeMerchantName.substring(0, 25));

  // Tag 60: Merchant City
  payload += formatTlv('60', 'BANGKOK');

  // Tag 62: Additional Data Field Template (ลบออกเมื่อเป็น Bill Payment เพื่อไม่ให้แสดง เลขที่อ้างอิง 3)
  if (!options.billerId) {
    let tag62Value = '';
    // Subtag 03: Store Label / Merchant Identifier
    tag62Value += formatTlv('03', activeMerchantCode.substring(0, 25));
    if (options.ref1) {
      tag62Value += formatTlv('07', options.ref1.substring(0, 25));
    }
    payload += formatTlv('62', tag62Value);
  }

  // Tag 63: CRC16 Checksum
  const checksumPayload = payload + '6304';
  const checksum = crc16(checksumPayload);
  return checksumPayload + checksum;
}

/**
 * Generate Base64 Data URL for PromptPay QR
 */
export async function generatePromptPayDataUrl(options: PromptPayQrParams): Promise<string> {
  const payload = generatePromptPayPayload(options);
  return QRCode.toDataURL(payload, {
    errorCorrectionLevel: 'M',
    margin: 2,
    width: 320,
    color: {
      dark: '#002D62', // Official Thai QR Navy Blue
      light: '#FFFFFF'
    }
  });
}

/**
 * Generate Branded Canvas for Download
 */
export async function generateDownloadablePromptPayImage(params: {
  qrDataUrl: string;
  orderId: string;
  amount: number;
  merchantName: string;
  merchantCode?: string;
  billerOrPromptPayId: string;
  ref1?: string;
  ref2?: string;
}): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 900;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return reject(new Error('Canvas 2D context not available'));
    }

    const activeMerchantCode = params.merchantCode || 'QV353070BI06881536X';

    // Background Card
    ctx.fillStyle = '#FFFFFF';
    ctx.roundRect ? ctx.roundRect(0, 0, 640, 900, 24) : ctx.fillRect(0, 0, 640, 900);
    ctx.fill();

    // Top Header Banner (Official Thai QR Gradient)
    const gradient = ctx.createLinearGradient(0, 0, 640, 0);
    gradient.addColorStop(0, '#002E6E');
    gradient.addColorStop(1, '#0054A6');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 640, 140);

    // Thai QR Payment Header Text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 30px "Prompt", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Thai QR Payment', 320, 55);

    ctx.font = '500 16px "Prompt", sans-serif';
    ctx.fillStyle = '#E0F2FE';
    ctx.fillText('พร้อมเพย์ / ชำระเงินผ่านแอปธนาคารทุกแห่ง', 320, 90);

    ctx.font = 'bold 15px "Prompt", sans-serif';
    ctx.fillStyle = '#FDE047';
    ctx.fillText('GIFFARINE ONLINE OFFICIAL STORE', 320, 118);

    // Merchant Info Box (Expanded with Merchant Code & Biller ID)
    ctx.fillStyle = '#F8FAFC';
    ctx.fillRect(40, 150, 560, 105);
    ctx.strokeStyle = '#E2E8F0';
    ctx.lineWidth = 1;
    ctx.strokeRect(40, 150, 560, 105);

    ctx.fillStyle = '#64748B';
    ctx.font = '13px "Prompt", sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('ชื่อบัญชีผู้รับเงิน:', 60, 173);
    ctx.fillText('รหัสสมาชิก (MID):', 60, 196);
    ctx.fillText('รหัสร้านค้า (Merchant Code):', 60, 219);
    ctx.fillText('รหัส Biller ID (KTB Tag 30):', 60, 242);

    ctx.fillStyle = '#0F172A';
    ctx.font = 'bold 13px "Prompt", sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(params.merchantName || 'GIFFFORYOU.COM', 580, 173);

    ctx.fillStyle = '#4338CA';
    ctx.font = 'bold 14px "Prompt", monospace';
    ctx.fillText('108037654', 580, 196);

    ctx.fillStyle = '#003366';
    ctx.font = 'bold 14px "Prompt", monospace';
    ctx.fillText(activeMerchantCode, 580, 219);

    ctx.fillStyle = '#0F172A';
    ctx.font = 'bold 14px "Prompt", monospace';
    ctx.fillText(params.billerOrPromptPayId || '010753700088205', 580, 242);

    // Amount Box
    ctx.fillStyle = '#FFF1F2';
    ctx.fillRect(40, 265, 560, 75);
    ctx.strokeStyle = '#FECDD3';
    ctx.strokeRect(40, 265, 560, 75);

    ctx.fillStyle = '#9F1239';
    ctx.font = 'bold 14px "Prompt", sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('จำนวนเงินที่ต้องชำระ (Total Amount):', 60, 308);

    ctx.fillStyle = '#E11D48';
    ctx.font = 'bold 30px "Prompt", sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`฿${params.amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 580, 312);

    // Load QR Code Image onto Canvas
    const qrImg = new Image();
    qrImg.crossOrigin = 'anonymous';
    qrImg.onload = () => {
      // Draw QR Image in Center
      const qrSize = 330;
      const qrX = (640 - qrSize) / 2;
      const qrY = 355;
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

      // Order Reference Footer Box
      ctx.fillStyle = '#F1F5F9';
      ctx.fillRect(40, 705, 560, 95);
      ctx.strokeStyle = '#CBD5E1';
      ctx.strokeRect(40, 705, 560, 95);

      ctx.fillStyle = '#1E293B';
      ctx.font = 'bold 14px "Prompt", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`MID: 108037654 • รหัสผู้ค้า (Ref 1): ${activeMerchantCode} • อ้างอิงคำสั่งซื้อ (Ref 2): #${params.orderId}`, 320, 735);

      ctx.font = '12px "Prompt", sans-serif';
      ctx.fillStyle = '#475569';
      ctx.fillText(`Biller ID: ${params.billerOrPromptPayId || '010753700088205'} • รองรับ KTB ถุงเงิน & Mobile Banking ทุกธนาคาร`, 320, 762);

      ctx.font = '11px "Prompt", sans-serif';
      ctx.fillStyle = '#64748B';
      ctx.fillText('ยอดเงินระบุตรงตามระบบอัตโนมัติ (Dynamic EMVCo QR Code)', 320, 785);

      // Bottom Watermark
      ctx.font = '11px "Prompt", sans-serif';
      ctx.fillStyle = '#94A3B8';
      ctx.fillText(`https://giffforyou.com • ออกเมื่อ ${new Date().toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' })}`, 320, 845);

      resolve(canvas.toDataURL('image/png'));
    };
    qrImg.onerror = (e) => reject(e);
    qrImg.src = params.qrDataUrl;
  });
}
